import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate } from '../api/dataverseClient';
import { Sprint, BurndownPoint } from '../types';

const SPRINT_FIELDS = [
  'dagrun_pm_sprintid','dagrun_pm_name','dagrun_pm_sprintgoal','dagrun_pm_startdate',
  'dagrun_pm_enddate','dagrun_pm_status','dagrun_pm_pointsplanned','dagrun_pm_pointscompleted',
  '_dagrun_pm_projectid_value',
];

// Sprint status picklist (verified via EntityDefinitions on prod).
export const SprintStatus = { Planned: 100000000, Active: 100000001, Completed: 100000002, Archived: 100000003 } as const;
export const SPRINT_STATUS_LABEL: Record<number, string> = {
  [SprintStatus.Planned]: 'Planned', [SprintStatus.Active]: 'Active',
  [SprintStatus.Completed]: 'Completed', [SprintStatus.Archived]: 'Archived',
};

/**
 * Demote any other Active sprint in the project to Planned, so only one
 * sprint is Active at a time. Call before saving a sprint as Active.
 */
export async function demoteOtherActiveSprints(
  pca: IPublicClientApplication, projectId: string, exceptSprintId: string | null,
): Promise<void> {
  const sprints = await getProjectSprints(pca, projectId);
  const others = sprints.filter(s => s.dagrun_pm_status === SprintStatus.Active && s.dagrun_pm_sprintid !== exceptSprintId);
  await Promise.all(others.map(s => updateSprint(pca, s.dagrun_pm_sprintid, { dagrun_pm_status: SprintStatus.Planned })));
}

export async function getProjectSprints(pca: IPublicClientApplication, projectId: string): Promise<Sprint[]> {
  return dvList<Sprint>(pca, 'dagrun_pm_sprints', {
    select: SPRINT_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_startdate desc',
  });
}

export async function getSprintBurndown(pca: IPublicClientApplication, sprintId: string): Promise<BurndownPoint[]> {
  return dvList<BurndownPoint>(pca, 'dagrun_pm_burndowns', {
    select: ['dagrun_pm_burndownid','dagrun_pm_snapshotdate','dagrun_pm_pointsremaining','dagrun_pm_idealremaining','_dagrun_pm_sprintid_value'],
    filter: `_dagrun_pm_sprintid_value eq ${sprintId}`,
    orderby: 'dagrun_pm_snapshotdate asc',
  });
}

export async function createSprint(
  pca: IPublicClientApplication, projectId: string,
  name: string, goal: string, startDate: string, endDate: string,
): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_sprints', {
    dagrun_pm_name: name,
    dagrun_pm_sprintgoal: goal,
    dagrun_pm_startdate: startDate,
    dagrun_pm_enddate: endDate,
    dagrun_pm_status: 100000000, // Planned
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  });
}

export async function updateSprint(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_sprints', id, patch);
}

export async function moveTaskToSprint(pca: IPublicClientApplication, taskId: string, sprintId: string | null): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_tasks', taskId, sprintId
    ? { 'dagrun_pm_sprintid@odata.bind': `/dagrun_pm_sprints(${sprintId})` }
    : { 'dagrun_pm_sprintid@odata.bind': null });
}
